import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Ban, FileText, Pencil, Plus, Printer, Undo2, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '../../context/AuthContext';
import { clientsAPI } from '../../lib/api';
import { fetchAllPages } from '../../lib/fetchAllPages';
import { stationsApi } from '../../lib/stationsApi';
import { messageErreur } from '../../lib/erreurs';
import { libelle, nomLisible, ton } from '../../lib/libelles';
import { accorde, formatDate, formatDateHeure, formatMois, formatMontant, formatNombre } from '../../lib/formats';
import { PageHeader } from '../../components/ui/page-header';
import { BarrePagination } from '../../components/ui/pagination-bar';
import { usePagination, usePaginationServeur } from '../../hooks/usePagination';
import { useDerniereDemande } from '../../hooks/useDerniereDemande';
import { useDebouncedValue } from '../../hooks/use-debounced-value';
import { Aide } from '../../components/ui/aide';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { LoadingState } from '../../components/ui/loading-state';
import { EmptyState } from '../../components/ui/empty-state';
import { useDroitsStation, useStations } from '../../components/stations/StationPicker';

/**
 * Clients et bons (lot 033) : comptes crédit, flottes et véhicules, bons
 * carburant avec code QR imprimable, relevés mensuels et paiements mobiles
 * (Orange Money, Moov Money, Wave). Les montants ne sont montrés qu'aux profils
 * qui voient les montants : les onglets Relevés et Paiements leur sont réservés.
 */

const ONGLETS = [
  ['comptes', 'Comptes', false],
  ['flottes', 'Flottes', false],
  ['bons', 'Bons', false],
  ['releves', 'Relevés', true],
  ['paiements', 'Paiements', true],
];

const MOYENS_MOBILES = ['ORANGE_MONEY', 'MOOV_MONEY', 'WAVE'];
const TOUS = '__tous';

const Champ = ({ id, label, aide, children }) => (
  <div className="grid gap-1.5">
    <label htmlFor={id} className="flex items-center gap-1 text-sm font-medium text-foreground">
      {label}
      {aide}
    </label>
    {children}
  </div>
);

const Panneau = ({ titre, aide, droite, children }) => (
  <section className="rounded-xl border border-border bg-card shadow-sm">
    <header className="flex flex-wrap items-baseline justify-between gap-2 px-4 pb-2 pt-3">
      <h2 className="flex items-center gap-1 text-base font-semibold">
        {titre}
        {aide}
      </h2>
      {droite}
    </header>
    <div className="overflow-x-auto px-1 pb-3">{children}</div>
  </section>
);

const CELLULES =
  'w-full border-collapse text-sm [&_td]:border-b [&_td]:border-border/60 [&_td]:px-3 [&_td]:py-2 [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-border [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground [&_tr:last-child_td]:border-0';

const Vide = ({ texte }) => <p className="px-3 py-2 text-sm text-muted-foreground">{texte}</p>;

const BoutonIcone = ({ label, onClick, children, disabled }) => (
  <Button type="button" variant="ghost" size="icon" title={label} aria-label={label} onClick={onClick} disabled={disabled}>
    {children}
  </Button>
);

const nombreOuVide = (valeur) => (valeur === '' || valeur === null || valeur === undefined ? undefined : Number(valeur));

/** Litres sans zéros inutiles : 50, 12,5. */
const litres = (valeur) => `${formatNombre(valeur, Number(valeur) % 1 ? 2 : 0)} L`;

const optionsClients = (liste) =>
  liste.map((client) => ({
    value: client.id,
    label: client.name,
    description: [client.phone, client.city].filter(Boolean).join(' · ') || undefined,
    keywords: [client.phone, client.city, client.contact_name].filter(Boolean),
  }));

/** Valeur d'un bon : litres, sinon francs (masqués sans droit sur les montants). */
const valeurBon = (bon) => {
  if (bon.liters !== null && bon.liters !== undefined) return litres(bon.liters);
  if (bon.amount !== null && bon.amount !== undefined) return formatMontant(bon.amount);
  return '—';
};

const lireFichier = (fichier) =>
  new Promise((resolve, reject) => {
    const lecteur = new FileReader();
    lecteur.onload = () => resolve(String(lecteur.result || '').split(',')[1] || '');
    lecteur.onerror = () => reject(lecteur.error);
    lecteur.readAsDataURL(fichier);
  });

// --- Impression (bons avec code QR, relevé mensuel) ----------------------------------------------

const echapper = (texte) =>
  String(texte ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const imprimer = (titre, styles, corps) => {
  const fenetre = window.open('', '_blank', 'width=900,height=700');
  if (!fenetre) {
    toast.error('Autorisez l’ouverture de fenêtres pour imprimer');
    return;
  }
  fenetre.document.write(
    `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>${echapper(titre)}</title><style>${styles}</style></head><body>${corps}</body></html>`,
  );
  fenetre.document.close();
  fenetre.focus();
  setTimeout(() => fenetre.print(), 300);
};

const STYLES_BONS = `
  body{font-family:Arial,sans-serif;margin:12mm;color:#111}
  .planche{display:grid;grid-template-columns:1fr 1fr;gap:6mm}
  .bon{border:1.5px dashed #444;border-radius:4mm;padding:5mm;display:flex;gap:5mm;align-items:center;break-inside:avoid;page-break-inside:avoid}
  .bon .texte{flex:1;min-width:0}
  .bon h2{margin:0 0 2mm;font-size:11pt;letter-spacing:.06em;text-transform:uppercase}
  .bon .numero{font-size:15pt;font-weight:700;margin:0 0 2mm}
  .bon .valeur{font-size:13pt;font-weight:700;margin:0 0 1mm}
  .bon p{margin:0 0 1mm;font-size:9.5pt}
  .bon .qr{width:34mm;height:34mm;flex:none}
  .bon .qr svg{width:100%;height:100%}
  @media print{body{margin:8mm}}
`;

const imprimerBons = (bons) => {
  const cartes = bons
    .map(
      (bon) => `
      <div class="bon">
        <div class="texte">
          <h2>Bon carburant</h2>
          <p class="numero">${echapper(bon.number)}</p>
          <p class="valeur">${echapper(valeurBon(bon))}${bon.product_name ? ` · ${echapper(bon.product_name)}` : ''}</p>
          <p>${echapper(bon.client_name || '')}</p>
          <p>${bon.expires_at ? `Valable jusqu’au ${echapper(formatDate(bon.expires_at))}` : 'Sans date limite'}</p>
        </div>
        <div class="qr">${bon.qr_svg || ''}</div>
      </div>`,
    )
    .join('');
  imprimer('Bons carburant', STYLES_BONS, `<div class="planche">${cartes}</div>`);
};

const STYLES_RELEVE = `
  body{font-family:Arial,sans-serif;margin:14mm;color:#111;font-size:10pt}
  h1{font-size:16pt;margin:0 0 2mm} h2{font-size:11pt;margin:6mm 0 2mm}
  table{width:100%;border-collapse:collapse} th,td{border-bottom:1px solid #ccc;padding:1.5mm 2mm;text-align:left}
  th{font-size:9pt;color:#555} .d{text-align:right;font-variant-numeric:tabular-nums}
  .total td{font-weight:700;border-top:2px solid #111}
`;

const imprimerReleve = (releve) => {
  const lignes = releve.lines
    .map(
      (ligne) => `<tr><td>${echapper(formatDateHeure(ligne.sale_date))}</td><td>${echapper(ligne.station_name || '')}</td>
        <td>${echapper(ligne.product_name || '')}</td><td>${echapper(ligne.vehicle_plate || '')}</td>
        <td class="d">${echapper(litres(ligne.quantity_liters))}</td><td class="d">${echapper(formatMontant(ligne.total_amount))}</td></tr>`,
    )
    .join('');
  const produits = releve.by_product
    .map(
      (produit) => `<tr><td>${echapper(produit.product_name || '')}</td><td class="d">${echapper(litres(produit.liters))}</td>
        <td class="d">${echapper(formatMontant(produit.amount))}</td></tr>`,
    )
    .join('');
  imprimer(
    `Relevé ${releve.label}`,
    STYLES_RELEVE,
    `<h1>Relevé des ventes à crédit — ${echapper(releve.label)}</h1>
     <p>${echapper(releve.client.name)}${releve.client.phone ? ` · ${echapper(releve.client.phone)}` : ''}</p>
     <h2>Ventes</h2>
     <table><thead><tr><th>Date</th><th>Station</th><th>Produit</th><th>Plaque</th><th class="d">Litres</th><th class="d">Montant</th></tr></thead>
     <tbody>${lignes}
     <tr class="total"><td colspan="4">Total</td><td class="d">${echapper(litres(releve.totals.liters))}</td><td class="d">${echapper(formatMontant(releve.totals.amount))}</td></tr></tbody></table>
     <h2>Par produit</h2>
     <table><thead><tr><th>Produit</th><th class="d">Litres</th><th class="d">Montant</th></tr></thead><tbody>${produits}</tbody></table>`,
  );
};

// --- Compte crédit ------------------------------------------------------------------------------------

const CompteDialog = ({ open, onOpenChange, clients, compte, peutFermer, onSaved }) => {
  const [clientId, setClientId] = useState('');
  const [plafond, setPlafond] = useState('');
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (!open) return;
    setClientId(compte?.id || '');
    setPlafond(compte?.station_credit_limit ? String(compte.station_credit_limit) : '');
  }, [open, compte]);
  const options = useMemo(() => optionsClients(clients), [clients]);

  const enregistrer = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      await stationsApi.credit.accounts.update(clientId, { creditLimit: Number(plafond) });
      toast.success('Compte crédit enregistré');
      onSaved(clientId);
      onOpenChange(false);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’enregistrer le compte'));
    } finally {
      setEnCours(false);
    }
  };

  const fermer = async () => {
    if (!window.confirm(`Fermer le compte crédit de « ${compte?.name} » ?`)) return;
    setEnCours(true);
    try {
      await stationsApi.credit.accounts.update(clientId, { creditLimit: null });
      toast.success('Compte crédit fermé');
      onSaved(clientId);
      onOpenChange(false);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de fermer le compte'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{compte ? 'Plafond de crédit' : 'Ouvrir un compte crédit'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="grid gap-4">
          <Champ id="compte-client" label="Client">
            <SearchableSelect
              id="compte-client"
              value={clientId}
              onValueChange={setClientId}
              options={options}
              aria-label="Client"
              placeholder="Choisir le client"
              searchPlaceholder="Rechercher un client…"
              emptyMessage="Aucun client"
              disabled={Boolean(compte)}
            />
          </Champ>
          <Champ
            id="compte-plafond"
            label="Plafond de crédit (FCFA)"
            aide={
              <Aide titre="Plafond de crédit">
                Montant maximal que le client peut devoir à la station : ventes à crédit et de flotte pas encore
                facturées, plus les factures non payées. Au-delà, la vente est refusée.
              </Aide>
            }
          >
            <Input
              id="compte-plafond"
              type="number"
              inputMode="numeric"
              min="1"
              step="1"
              value={plafond}
              onChange={(event) => setPlafond(event.target.value)}
              required
              className="text-right tabular-nums"
            />
          </Champ>
          <DialogFooter className="gap-2 sm:justify-between">
            {compte && peutFermer ? (
              <Button type="button" variant="outline" onClick={fermer} disabled={enCours}>
                Fermer le compte
              </Button>
            ) : (
              <span />
            )}
            <span className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={enCours || !clientId || !plafond}>
                Enregistrer
              </Button>
            </span>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const OngletComptes = ({ comptes, chargement, argent, peutEcrire, onModifier, onBloquer, onFlotte, onReleve }) => {
  const [recherche, setRecherche] = useState('');
  const filtres = useMemo(() => {
    const mot = recherche.trim().toLowerCase();
    if (!mot) return comptes;
    return comptes.filter((compte) => `${compte.name || ''} ${compte.phone || ''}`.toLowerCase().includes(mot));
  }, [comptes, recherche]);
  const pagination = usePagination(filtres, { cle: 'comptes', reinitialiser: recherche.trim().toLowerCase() });

  if (chargement) return <LoadingState shape="table" />;
  if (!comptes.length) {
    return <EmptyState variant="empty" title="Aucun compte crédit" className="border-0" />;
  }
  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={recherche}
          onChange={(event) => setRecherche(event.target.value)}
          placeholder="Nom ou téléphone du client"
          aria-label="Rechercher un compte"
          className="h-9 w-full sm:w-64"
        />
      </div>
      <div>
        <Panneau titre="Comptes crédit">
          {filtres.length === 0 ? (
            <Vide texte="Aucun compte ne correspond" />
          ) : (
            <table className={CELLULES}>
              <thead>
                <tr>
                  <th className="text-left">Client</th>
                  {argent && <th className="text-right">Plafond</th>}
                  {argent && <th className="text-right">Encours</th>}
                  {argent && <th className="text-right">Disponible</th>}
                  <th className="text-right">Véhicules</th>
                  <th className="text-left">État</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {pagination.lignes.map((compte) => (
                  <tr key={compte.id} className={compte.station_credit_blocked || compte.over_limit ? 'bg-destructive/5' : undefined}>
                    <td className="font-medium">
                      {compte.name}
                      {compte.phone ? <span className="block text-xs font-normal text-muted-foreground">{compte.phone}</span> : null}
                    </td>
                    {argent && (
                      <td className="text-right tabular-nums">
                        {compte.has_account ? formatMontant(compte.station_credit_limit) : '—'}
                      </td>
                    )}
                    {argent && <td className="text-right tabular-nums">{formatMontant(compte.outstanding)}</td>}
                    {argent && (
                      <td className="text-right tabular-nums">{compte.has_account ? formatMontant(compte.available) : '—'}</td>
                    )}
                    <td className="text-right tabular-nums">{formatNombre(compte.vehicle_count)}</td>
                    <td>
                      {compte.station_credit_blocked ? (
                        <Badge variant="destructive">Bloqué</Badge>
                      ) : compte.over_limit ? (
                        <Badge variant="warning">Plafond dépassé</Badge>
                      ) : compte.has_account ? (
                        <Badge variant="success">Actif</Badge>
                      ) : (
                        <Badge variant="muted">Fermé</Badge>
                      )}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      {peutEcrire && argent && (
                        <BoutonIcone label="Modifier le plafond" onClick={() => onModifier(compte)}>
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                        </BoutonIcone>
                      )}
                      {peutEcrire && (
                        <BoutonIcone
                          label={compte.station_credit_blocked ? 'Débloquer le compte' : 'Bloquer le compte'}
                          onClick={() => onBloquer(compte)}
                        >
                          {compte.station_credit_blocked ? (
                            <Undo2 className="h-4 w-4" aria-hidden="true" />
                          ) : (
                            <Ban className="h-4 w-4" aria-hidden="true" />
                          )}
                        </BoutonIcone>
                      )}
                      <Button type="button" variant="outline" size="sm" onClick={() => onFlotte(compte.id)}>
                        Flotte
                      </Button>
                      {argent && (
                        <Button type="button" variant="outline" size="sm" className="ml-1" onClick={() => onReleve(compte.id)}>
                          Relevé
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panneau>
        <BarrePagination pagination={pagination} nom="comptes" />
      </div>
    </div>
  );
};

// --- Flottes ---------------------------------------------------------------------------------------------

const VEHICULE_VIDE = { plate: '', label: '', product_ids: [], daily_liters: '', monthly_liters: '' };

const VehiculeDialog = ({ open, onOpenChange, clientId, vehicule, produits, onSaved }) => {
  const [form, setForm] = useState(VEHICULE_VIDE);
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(
      vehicule
        ? {
            plate: vehicule.plate,
            label: vehicule.label || '',
            product_ids: vehicule.product_ids || [],
            daily_liters: vehicule.daily_liters ?? '',
            monthly_liters: vehicule.monthly_liters ?? '',
          }
        : VEHICULE_VIDE,
    );
  }, [open, vehicule]);
  const changer = (cle) => (event) => setForm((avant) => ({ ...avant, [cle]: event?.target ? event.target.value : event }));
  const basculerProduit = (produitId, coche) =>
    setForm((avant) => ({
      ...avant,
      product_ids: coche ? [...avant.product_ids, produitId] : avant.product_ids.filter((id) => id !== produitId),
    }));

  const enregistrer = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      const corps = {
        label: form.label.trim(),
        product_ids: form.product_ids,
        daily_liters: nombreOuVide(form.daily_liters) ?? null,
        monthly_liters: nombreOuVide(form.monthly_liters) ?? null,
      };
      if (vehicule) await stationsApi.credit.vehicles.update(vehicule.id, corps);
      else await stationsApi.credit.vehicles.create({ ...corps, client_id: clientId, plate: form.plate.trim() });
      toast.success(vehicule ? 'Véhicule modifié' : 'Véhicule ajouté');
      onSaved();
      onOpenChange(false);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’enregistrer le véhicule'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{vehicule ? 'Modifier le véhicule' : 'Ajouter un véhicule'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="grid gap-4 sm:grid-cols-2">
          <Champ id="vehicule-plaque" label="Plaque">
            <Input id="vehicule-plaque" value={form.plate} onChange={changer('plate')} required disabled={Boolean(vehicule)} maxLength={30} />
          </Champ>
          <Champ id="vehicule-libelle" label="Précision (chauffeur, modèle)">
            <Input id="vehicule-libelle" value={form.label} onChange={changer('label')} maxLength={100} />
          </Champ>
          <Champ id="vehicule-jour" label="Litres par jour">
            <Input
              id="vehicule-jour"
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              value={form.daily_liters}
              onChange={changer('daily_liters')}
              className="text-right tabular-nums"
            />
          </Champ>
          <Champ id="vehicule-mois" label="Litres par mois">
            <Input
              id="vehicule-mois"
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              value={form.monthly_liters}
              onChange={changer('monthly_liters')}
              className="text-right tabular-nums"
            />
          </Champ>
          <div className="grid gap-1.5 sm:col-span-2">
            <span className="flex items-center gap-1 text-sm font-medium">
              Produits autorisés
              <Aide titre="Produits autorisés">Aucun produit coché : le véhicule peut prendre tous les produits.</Aide>
            </span>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {produits.map((produit) => (
                <label key={produit.id} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={form.product_ids.includes(produit.id)}
                    onCheckedChange={(coche) => basculerProduit(produit.id, coche === true)}
                    aria-label={produit.name}
                  />
                  {produit.name}
                </label>
              ))}
            </div>
          </div>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || !form.plate.trim()}>
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const OngletFlottes = ({ comptes, clientId, onClient, produits, peutEcrire }) => {
  const [vehicules, setVehicules] = useState([]);
  const [totalVehicules, setTotalVehicules] = useState(0);
  const [chargement, setChargement] = useState(false);
  const [dialogue, setDialogue] = useState({ ouvert: false, vehicule: null });
  const options = useMemo(() => optionsClients(comptes), [comptes]);
  const noms = useMemo(() => Object.fromEntries(produits.map((produit) => [produit.id, produit.name])), [produits]);
  const pagination = usePaginationServeur(totalVehicules, { cle: 'flotte', reinitialiser: clientId });
  const { page, taille } = pagination;

  const nouvelleDemande = useDerniereDemande();
  const charger = useCallback(async () => {
    const perimee = nouvelleDemande();
    if (!clientId) {
      setVehicules([]);
      setTotalVehicules(0);
      return;
    }
    setChargement(true);
    try {
      const reponse = await stationsApi.credit.vehicles.page(clientId, { page, page_size: taille });
      if (perimee()) return;
      setVehicules(reponse.items);
      setTotalVehicules(reponse.total);
    } catch (erreur) {
      if (perimee()) return;
      toast.error(messageErreur(erreur, 'Impossible de charger la flotte'));
    } finally {
      if (!perimee()) setChargement(false);
    }
  }, [clientId, page, taille, nouvelleDemande]);

  useEffect(() => {
    charger();
  }, [charger]);

  const basculer = async (vehicule) => {
    try {
      await stationsApi.credit.vehicles.update(vehicule.id, { active: !vehicule.active });
      await charger();
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de modifier le véhicule'));
    }
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <SearchableSelect
          value={clientId}
          onValueChange={onClient}
          options={options}
          aria-label="Client"
          placeholder="Choisir le client"
          searchPlaceholder="Rechercher un client…"
          emptyMessage="Aucun compte crédit"
          className="h-9 w-full sm:w-72"
        />
        {peutEcrire && clientId && (
          <Button type="button" onClick={() => setDialogue({ ouvert: true, vehicule: null })}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Ajouter un véhicule
          </Button>
        )}
      </div>
      {!clientId ? (
        <EmptyState variant="empty" title="Choisissez un client" className="border-0" />
      ) : chargement && !vehicules.length ? (
        <LoadingState shape="table" />
      ) : (
        <Panneau titre="Véhicules de la flotte">
          {vehicules.length === 0 ? (
            <Vide texte="Aucun véhicule" />
          ) : (
            <table className={CELLULES}>
              <thead>
                <tr>
                  <th className="text-left">Plaque</th>
                  <th className="text-left">Produits autorisés</th>
                  <th className="text-right">Litres par jour</th>
                  <th className="text-right">Litres par mois</th>
                  <th className="text-left">État</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {vehicules.map((vehicule) => (
                  <tr key={vehicule.id} className={vehicule.active ? undefined : 'text-muted-foreground'}>
                    <td className="font-medium">
                      {vehicule.plate}
                      {vehicule.label ? <span className="block text-xs font-normal text-muted-foreground">{vehicule.label}</span> : null}
                    </td>
                    <td>
                      {(vehicule.product_ids || []).length
                        ? vehicule.product_ids.map((id) => noms[id] || 'Produit').join(', ')
                        : 'Tous'}
                    </td>
                    <td className="text-right tabular-nums">{vehicule.daily_liters ? litres(vehicule.daily_liters) : '—'}</td>
                    <td className="text-right tabular-nums">{vehicule.monthly_liters ? litres(vehicule.monthly_liters) : '—'}</td>
                    <td>{vehicule.active ? <Badge variant="success">Actif</Badge> : <Badge variant="muted">Retiré</Badge>}</td>
                    <td className="whitespace-nowrap text-right">
                      {peutEcrire && (
                        <>
                          <BoutonIcone label="Modifier le véhicule" onClick={() => setDialogue({ ouvert: true, vehicule })}>
                            <Pencil className="h-4 w-4" aria-hidden="true" />
                          </BoutonIcone>
                          <Button type="button" variant="outline" size="sm" onClick={() => basculer(vehicule)}>
                            {vehicule.active ? 'Retirer' : 'Remettre'}
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panneau>
      )}
      {clientId && totalVehicules > 0 ? <BarrePagination pagination={pagination} nom="véhicules" /> : null}
      <VehiculeDialog
        open={dialogue.ouvert}
        onOpenChange={(ouvert) => !ouvert && setDialogue({ ouvert: false, vehicule: null })}
        clientId={clientId}
        vehicule={dialogue.vehicule}
        produits={produits}
        onSaved={charger}
      />
    </div>
  );
};

// --- Bons ---------------------------------------------------------------------------------------------------

const DEMAIN = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
};

const EmettreDialog = ({ open, onOpenChange, clients, produits, argent, onIssued }) => {
  const [form, setForm] = useState({ clientId: '', productId: '', genre: 'litres', valeur: '', nombre: '1', fin: '' });
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (open) setForm({ clientId: '', productId: '', genre: 'litres', valeur: '', nombre: '1', fin: '' });
  }, [open]);
  const changer = (cle) => (event) => setForm((avant) => ({ ...avant, [cle]: event?.target ? event.target.value : event }));
  const optionsProduits = useMemo(
    () => produits.filter((produit) => produit.is_active && produit.unit === 'L').map((produit) => ({ value: produit.id, label: produit.name })),
    [produits],
  );
  const optionsGenre = [
    { value: 'litres', label: 'En litres' },
    ...(argent ? [{ value: 'francs', label: 'En francs' }] : []),
  ];

  const emettre = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      const bons = await stationsApi.credit.vouchers.issue({
        clientId: form.clientId,
        productId: form.productId || undefined,
        count: Number(form.nombre),
        liters: form.genre === 'litres' ? Number(form.valeur) : undefined,
        amount: form.genre === 'francs' ? Number(form.valeur) : undefined,
        expiresAt: form.fin ? `${form.fin}T23:59:59Z` : undefined,
      });
      onIssued(bons);
      onOpenChange(false);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’émettre les bons'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">
            Émettre des bons
            <Aide titre="Bons carburant">
              Chaque bon porte un numéro et un code QR. Il ne sert qu’une fois : la vente qui l’encaisse le consomme.
            </Aide>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={emettre} className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Champ id="bon-client" label="Client">
              <SearchableSelect
                id="bon-client"
                value={form.clientId}
                onValueChange={changer('clientId')}
                options={optionsClients(clients)}
                aria-label="Client"
                placeholder="Choisir le client"
                searchPlaceholder="Rechercher un client…"
                emptyMessage="Aucun client"
              />
            </Champ>
          </div>
          <Champ id="bon-produit" label="Produit">
            <SearchableSelect id="bon-produit" value={form.productId} onValueChange={changer('productId')} options={optionsProduits} aria-label="Produit" placeholder="Choisir le produit" />
          </Champ>
          <Champ id="bon-genre" label="Valeur du bon">
            <SearchableSelect id="bon-genre" value={form.genre} onValueChange={changer('genre')} options={optionsGenre} aria-label="Valeur du bon" />
          </Champ>
          <Champ id="bon-valeur" label={form.genre === 'litres' ? 'Litres par bon' : 'Francs par bon'}>
            <Input
              id="bon-valeur"
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              value={form.valeur}
              onChange={changer('valeur')}
              required
              className="text-right tabular-nums"
            />
          </Champ>
          <Champ id="bon-nombre" label="Nombre de bons">
            <Input
              id="bon-nombre"
              type="number"
              inputMode="numeric"
              min="1"
              max="200"
              step="1"
              value={form.nombre}
              onChange={changer('nombre')}
              required
              className="text-right tabular-nums"
            />
          </Champ>
          <Champ id="bon-fin" label="Valable jusqu’au">
            <Input id="bon-fin" type="date" min={DEMAIN()} value={form.fin} onChange={changer('fin')} />
          </Champ>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || !form.clientId || !form.valeur || !form.nombre || (form.genre === 'litres' && !form.productId)}>
              Émettre
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AnnulerBonDialog = ({ bon, onClose, onDone }) => {
  const [motif, setMotif] = useState('');
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (bon) setMotif('');
  }, [bon]);
  const annuler = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      await stationsApi.credit.vouchers.cancel(bon.id, motif.trim());
      toast.success('Bon annulé');
      onDone();
      onClose();
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’annuler le bon'));
    } finally {
      setEnCours(false);
    }
  };
  return (
    <Dialog open={Boolean(bon)} onOpenChange={(ouvert) => !ouvert && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Annuler le bon {bon?.number}</DialogTitle>
        </DialogHeader>
        <form onSubmit={annuler} className="grid gap-4">
          <Champ id="bon-motif" label="Motif">
            <Input id="bon-motif" value={motif} onChange={(event) => setMotif(event.target.value)} maxLength={300} required />
          </Champ>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Retour
            </Button>
            <Button type="submit" variant="destructive" disabled={enCours || !motif.trim()}>
              Annuler le bon
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const OngletBons = ({ bons, total, pagination, chargement, clients, produits, peutEcrire, filtre, onFiltre, selection, onSelection, onRecharger, onImprimer }) => {
  const [aAnnuler, setAAnnuler] = useState(null);
  const nomsClients = useMemo(() => Object.fromEntries(clients.map((client) => [client.id, client.name])), [clients]);
  const nomsProduits = useMemo(() => Object.fromEntries(produits.map((produit) => [produit.id, produit.name])), [produits]);
  const optionsClientsFiltre = useMemo(() => [{ value: TOUS, label: 'Tous les clients' }, ...optionsClients(clients)], [clients]);
  const optionsEtat = [
    { value: TOUS, label: 'Tous les états' },
    { value: 'ISSUED', label: libelle('station_bon', 'ISSUED') },
    { value: 'USED', label: libelle('station_bon', 'USED') },
    { value: 'CANCELLED', label: libelle('station_bon', 'CANCELLED') },
    { value: 'EXPIRED', label: libelle('station_bon', 'EXPIRED') },
  ];
  const basculer = (id, coche) => {
    const suite = new Set(selection);
    if (coche) suite.add(id);
    else suite.delete(id);
    onSelection(suite);
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <SearchableSelect
          value={filtre.clientId || TOUS}
          onValueChange={(valeur) => onFiltre({ ...filtre, clientId: valeur === TOUS ? '' : valeur })}
          options={optionsClientsFiltre}
          aria-label="Client"
          searchPlaceholder="Rechercher un client…"
          className="h-9 w-full sm:w-64"
        />
        <SearchableSelect
          value={filtre.status || TOUS}
          onValueChange={(valeur) => onFiltre({ ...filtre, status: valeur === TOUS ? '' : valeur })}
          options={optionsEtat}
          aria-label="État"
          className="h-9 w-full sm:w-44"
        />
        <Input
          value={filtre.search}
          onChange={(event) => onFiltre({ ...filtre, search: event.target.value })}
          placeholder="Numéro du bon ou client"
          aria-label="Numéro du bon ou client"
          className="h-9 w-full sm:w-44"
        />
        {selection.size > 0 && (
          <Button type="button" variant="outline" size="sm" onClick={() => onImprimer([...selection])}>
            <Printer className="h-4 w-4" aria-hidden="true" />
            Imprimer la sélection ({selection.size})
          </Button>
        )}
      </div>
      {chargement && !bons.length ? (
        <LoadingState shape="table" />
      ) : (
        <Panneau titre="Bons carburant">
          {bons.length === 0 ? (
            <Vide texte="Aucun bon" />
          ) : (
            <table className={CELLULES}>
              <thead>
                <tr>
                  <th aria-label="Sélection" />
                  <th className="text-left">Numéro</th>
                  <th className="text-left">Client</th>
                  <th className="text-left">Produit</th>
                  <th className="text-right">Valeur</th>
                  <th className="text-left">Émis le</th>
                  <th className="text-left">Valable jusqu’au</th>
                  <th className="text-left">État</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {bons.map((bon) => (
                  <tr key={bon.id}>
                    <td>
                      <Checkbox
                        checked={selection.has(bon.id)}
                        onCheckedChange={(coche) => basculer(bon.id, coche === true)}
                        aria-label={`Sélectionner le bon ${bon.number}`}
                      />
                    </td>
                    <td className="whitespace-nowrap font-medium">{bon.number}</td>
                    <td>{nomsClients[bon.client_id] || 'Client'}</td>
                    <td>{nomsProduits[bon.product_id] || '—'}</td>
                    <td className="whitespace-nowrap text-right tabular-nums">{valeurBon(bon)}</td>
                    <td className="whitespace-nowrap">{formatDate(bon.issued_at)}</td>
                    <td className="whitespace-nowrap">{bon.expires_at ? formatDate(bon.expires_at) : '—'}</td>
                    <td>
                      <Badge variant={ton('station_bon', bon.effective_status)}>{libelle('station_bon', bon.effective_status)}</Badge>
                      {bon.status === 'USED' && bon.used_at ? (
                        <span className="block text-xs text-muted-foreground">{formatDateHeure(bon.used_at)}</span>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      <BoutonIcone label={`Imprimer le bon ${bon.number}`} onClick={() => onImprimer([bon.id])}>
                        <Printer className="h-4 w-4" aria-hidden="true" />
                      </BoutonIcone>
                      {peutEcrire && bon.status === 'ISSUED' && (
                        <BoutonIcone label={`Annuler le bon ${bon.number}`} onClick={() => setAAnnuler(bon)}>
                          <Ban className="h-4 w-4" aria-hidden="true" />
                        </BoutonIcone>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panneau>
      )}
      {total > 0 ? <BarrePagination pagination={pagination} nom="bons" /> : null}
      <AnnulerBonDialog bon={aAnnuler} onClose={() => setAAnnuler(null)} onDone={onRecharger} />
    </div>
  );
};

// --- Relevés mensuels --------------------------------------------------------------------------------------------

const moisRecents = () => {
  const aujourdhui = new Date();
  return Array.from({ length: 24 }, (_, i) => {
    const date = new Date(aujourdhui.getFullYear(), aujourdhui.getMonth() - i, 1);
    const valeur = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return { value: valeur, label: formatMois(valeur) };
  });
};

const OngletReleves = ({ comptes, clientId, onClient, peutEcrire }) => {
  const mois = useMemo(moisRecents, []);
  const [periode, setPeriode] = useState(mois[0].value);
  const [releve, setReleve] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const options = useMemo(() => optionsClients(comptes), [comptes]);
  // Les lignes de vente du mois se lisent page par page; l'impression, elle, les prend toutes.
  const pagination = usePaginationServeur(releve?.lines_total ?? 0, {
    cle: 'ventes',
    reinitialiser: `${clientId}|${periode}`,
  });
  const { page, taille } = pagination;

  const nouvelleDemande = useDerniereDemande();
  const charger = useCallback(async () => {
    const perimee = nouvelleDemande();
    if (!clientId) {
      setReleve(null);
      return;
    }
    const [annee, numero] = periode.split('-').map(Number);
    setChargement(true);
    try {
      const resultat = await stationsApi.credit.statements.get(clientId, annee, numero, { page, page_size: taille });
      if (perimee()) return;
      setReleve(resultat);
    } catch (erreur) {
      if (perimee()) return;
      setReleve(null);
      toast.error(messageErreur(erreur, 'Impossible de charger le relevé'));
    } finally {
      if (!perimee()) setChargement(false);
    }
  }, [clientId, periode, page, taille, nouvelleDemande]);

  const imprimerTout = async () => {
    const [annee, numero] = periode.split('-').map(Number);
    try {
      imprimerReleve(await stationsApi.credit.statements.get(clientId, annee, numero));
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de préparer l’impression'));
    }
  };

  useEffect(() => {
    charger();
  }, [charger]);

  const facturer = async () => {
    const aFacturer = releve.totals.to_invoice_amount;
    if (!window.confirm(`Facturer ${formatMontant(aFacturer)} de ventes à crédit pour ${releve.label} ?`)) return;
    setEnCours(true);
    try {
      const [annee, numero] = periode.split('-').map(Number);
      await stationsApi.credit.statements.invoice(clientId, annee, numero);
      toast.success('Relevé facturé');
      await charger();
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de facturer le relevé'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <SearchableSelect
          value={clientId}
          onValueChange={onClient}
          options={options}
          aria-label="Client"
          placeholder="Choisir le client"
          searchPlaceholder="Rechercher un client…"
          emptyMessage="Aucun compte crédit"
          className="h-9 w-full sm:w-72"
        />
        <SearchableSelect value={periode} onValueChange={setPeriode} options={mois} aria-label="Mois" className="h-9 w-full sm:w-52" />
        {releve && releve.totals.sale_count > 0 && (
          <Button type="button" variant="outline" size="sm" onClick={imprimerTout}>
            <Printer className="h-4 w-4" aria-hidden="true" />
            Imprimer
          </Button>
        )}
        {peutEcrire && releve && releve.totals.to_invoice_amount > 0 && (
          <Button type="button" size="sm" onClick={facturer} disabled={enCours}>
            <FileText className="h-4 w-4" aria-hidden="true" />
            Facturer {formatMontant(releve.totals.to_invoice_amount)}
          </Button>
        )}
      </div>

      {!clientId ? (
        <EmptyState variant="empty" title="Choisissez un client" className="border-0" />
      ) : chargement && !releve ? (
        <LoadingState shape="table" />
      ) : releve ? (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
          <Panneau
            titre={`Ventes — ${releve.label}`}
            droite={
              <span className="text-xs text-muted-foreground">
                {accorde(releve.totals.sale_count, 'vente')} · {litres(releve.totals.liters)} · {formatMontant(releve.totals.amount)}
              </span>
            }
          >
            {releve.totals.sale_count === 0 ? (
              <Vide texte="Aucune vente à crédit ce mois-ci" />
            ) : (
              <table className={CELLULES}>
                <thead>
                  <tr>
                    <th className="text-left">Date</th>
                    <th className="text-left">Station</th>
                    <th className="text-left">Produit</th>
                    <th className="text-left">Plaque</th>
                    <th className="text-left">Paiement</th>
                    <th className="text-right">Litres</th>
                    <th className="text-right">Montant</th>
                    <th className="text-left">Facture</th>
                  </tr>
                </thead>
                <tbody>
                  {releve.lines.map((ligne) => (
                    <tr key={ligne.sale_id}>
                      <td className="whitespace-nowrap">{formatDateHeure(ligne.sale_date)}</td>
                      <td>{ligne.station_name || '—'}</td>
                      <td>{ligne.product_name || '—'}</td>
                      <td>{ligne.vehicle_plate || '—'}</td>
                      <td>{libelle('station_paiement', ligne.payment_method)}</td>
                      <td className="whitespace-nowrap text-right tabular-nums">{litres(ligne.quantity_liters)}</td>
                      <td className="whitespace-nowrap text-right tabular-nums">{formatMontant(ligne.total_amount)}</td>
                      <td>{ligne.invoiced ? <Badge variant="success">Facturée</Badge> : <Badge variant="warning">À facturer</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <BarrePagination pagination={pagination} nom="ventes" className="mx-3" />
          </Panneau>
          <div className="grid content-start gap-4">
            <Panneau titre="Par produit">
              {releve.by_product.length === 0 ? (
                <Vide texte="Aucune vente" />
              ) : (
                <table className={CELLULES}>
                  <thead>
                    <tr>
                      <th className="text-left">Produit</th>
                      <th className="text-right">Litres</th>
                      <th className="text-right">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {releve.by_product.map((produit) => (
                      <tr key={produit.product_id || produit.product_name}>
                        <td>{produit.product_name || '—'}</td>
                        <td className="text-right tabular-nums">{litres(produit.liters)}</td>
                        <td className="text-right tabular-nums">{formatMontant(produit.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panneau>
            <Panneau titre="Factures du mois">
              {releve.invoices.length === 0 ? (
                <Vide texte="Aucune facture" />
              ) : (
                <table className={CELLULES}>
                  <thead>
                    <tr>
                      <th className="text-left">Facture</th>
                      <th className="text-right">Montant</th>
                      <th className="text-right">Reste à payer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {releve.invoices.map((facture) => (
                      <tr key={facture.id}>
                        <td className="whitespace-nowrap">
                          {facture.invoice_number}
                          <span className="block text-xs text-muted-foreground">{formatDate(facture.invoice_date)}</span>
                        </td>
                        <td className="text-right tabular-nums">{formatMontant(facture.total_amount)}</td>
                        <td className="text-right tabular-nums">{formatMontant(facture.amount_remaining)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panneau>
          </div>
        </div>
      ) : null}
    </div>
  );
};

// --- Paiements mobiles et moyens de paiement ----------------------------------------------------------------------

const MoyensPaiement = ({ methodes, peutEcrire, onChange }) => {
  const modifier = async (methode, corps) => {
    try {
      await stationsApi.credit.methods.update(methode.code, corps);
      onChange();
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de modifier le moyen de paiement'));
    }
  };
  return (
    <Panneau
      titre="Moyens de paiement"
      aide={
        <Aide titre="Moyens de paiement">
          Seuls les paiements mobiles et la carte se désactivent. Les frais de l’opérateur sont en pour cent de la somme encaissée.
        </Aide>
      }
    >
      <table className={CELLULES}>
        <thead>
          <tr>
            <th className="text-left">Moyen</th>
            <th className="text-right">Frais (%)</th>
            <th className="text-right">Utilisé</th>
          </tr>
        </thead>
        <tbody>
          {methodes.map((methode) => {
            const reglable = methode.kind === 'MOBILE_MONEY' || methode.kind === 'CARD';
            return (
              <tr key={methode.code}>
                <td>{libelle('station_paiement', methode.code)}</td>
                <td className="text-right">
                  {reglable ? (
                    <Input
                      key={`${methode.code}-${methode.fee_percent}`}
                      type="number"
                      inputMode="decimal"
                      min="0"
                      max="20"
                      step="0.01"
                      defaultValue={methode.fee_percent}
                      disabled={!peutEcrire}
                      aria-label={`Frais ${libelle('station_paiement', methode.code)}`}
                      className="ml-auto h-8 w-24 text-right tabular-nums"
                      onBlur={(event) => {
                        const valeur = Number(event.target.value);
                        if (event.target.value !== '' && valeur !== Number(methode.fee_percent)) modifier(methode, { fee_percent: valeur });
                      }}
                    />
                  ) : (
                    '—'
                  )}
                </td>
                <td className="text-right">
                  <Switch
                    checked={Boolean(methode.active)}
                    disabled={!peutEcrire || !reglable}
                    onCheckedChange={(actif) => modifier(methode, { active: actif })}
                    aria-label={`Utiliser ${libelle('station_paiement', methode.code)}`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Panneau>
  );
};

const DetailImport = ({ detail, onFermer, onRapprocher, peutEcrire, enCours }) => {
  // Un relevé peut porter des centaines de sommes: elles se lisent par pages de 20, 50 ou 100.
  const paginationSommes = usePagination(detail?.lines, { cle: 'sommes', reinitialiser: detail?.id || '' });
  const paginationSansPaiement = usePagination(detail?.sales_without_payment, {
    cle: 'sans-paiement',
    reinitialiser: detail?.id || '',
  });
  return (
    <Dialog open={Boolean(detail)} onOpenChange={(ouvert) => !ouvert && onFermer()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {detail ? `${libelle('station_paiement', detail.method_code)} — ${detail.filename || 'relevé'}` : ''}
          </DialogTitle>
        </DialogHeader>
        {detail && (
          <div className="grid gap-4">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="success">{accorde(detail.matched_count, 'rapprochée')}</Badge>
              <Badge variant={detail.unmatched_count ? 'warning' : 'muted'}>
                {accorde(detail.unmatched_count, 'somme sans vente', 'sommes sans vente')} · {formatMontant(detail.unmatched_amount)}
              </Badge>
              <Badge variant={detail.sales_without_payment.length ? 'warning' : 'muted'}>
                {accorde(detail.sales_without_payment.length, 'vente sans paiement reçu', 'ventes sans paiement reçu')}
              </Badge>
              {peutEcrire && (
                <Button type="button" variant="outline" size="sm" className="ml-auto" onClick={onRapprocher} disabled={enCours}>
                  Rapprocher à nouveau
                </Button>
              )}
            </div>
            <Panneau titre="Sommes reçues">
              <table className={CELLULES}>
                <thead>
                  <tr>
                    <th className="text-left">Heure</th>
                    <th className="text-left">Référence</th>
                    <th className="text-right">Montant</th>
                    <th className="text-left">État</th>
                    <th className="text-left">Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {paginationSommes.lignes.map((ligne) => (
                    <tr key={ligne.key}>
                      <td className="whitespace-nowrap">{ligne.has_time ? formatDateHeure(ligne.occurred_at) : formatDate(ligne.occurred_at)}</td>
                      <td>{ligne.reference || ligne.counterparty || '—'}</td>
                      <td className="whitespace-nowrap text-right tabular-nums">{formatMontant(ligne.amount)}</td>
                      <td>
                        <Badge variant={ton('station_rapprochement', ligne.status)}>{libelle('station_rapprochement', ligne.status)}</Badge>
                      </td>
                      <td>
                        {ligne.matched_by ? libelle('station_rapprochement_par', ligne.matched_by) : '—'}
                        {ligne.ambiguous ? <span className="ml-1 text-xs text-muted-foreground">· à vérifier</span> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <BarrePagination pagination={paginationSommes} nom="sommes" className="mx-3" />
            </Panneau>
            {detail.sales_without_payment.length > 0 && (
              <Panneau titre="Ventes sans paiement reçu">
                <table className={CELLULES}>
                  <thead>
                    <tr>
                      <th className="text-left">Heure</th>
                      <th className="text-left">Station</th>
                      <th className="text-left">Plaque</th>
                      <th className="text-right">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginationSansPaiement.lignes.map((vente) => (
                      <tr key={vente.id}>
                        <td className="whitespace-nowrap">{formatDateHeure(vente.sale_date)}</td>
                        <td>{vente.station_name || '—'}</td>
                        <td>{vente.vehicle_plate || '—'}</td>
                        <td className="text-right tabular-nums">{formatMontant(vente.total_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <BarrePagination pagination={paginationSansPaiement} nom="ventes" className="mx-3" />
              </Panneau>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const OngletPaiements = ({ stations, peutEcrire }) => {
  const [methodes, setMethodes] = useState([]);
  const [imports, setImports] = useState([]);
  const [totalImports, setTotalImports] = useState(0);
  const [chargement, setChargement] = useState(true);
  const pagination = usePaginationServeur(totalImports, { cle: 'releves' });
  const { page, taille } = pagination;
  const [methode, setMethode] = useState('ORANGE_MONEY');
  const [stationId, setStationId] = useState(TOUS);
  const [fichier, setFichier] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [detail, setDetail] = useState(null);

  const nouvelleDemande = useDerniereDemande();
  const charger = useCallback(async () => {
    const perimee = nouvelleDemande();
    const [lesMethodes, lesImports] = await Promise.allSettled([
      stationsApi.credit.methods.list(),
      stationsApi.credit.imports.page({ page, page_size: taille }),
    ]);
    if (perimee()) return;
    if (lesMethodes.status === 'rejected') toast.error(messageErreur(lesMethodes.reason, 'Impossible de charger les moyens de paiement'));
    if (lesImports.status === 'rejected') toast.error(messageErreur(lesImports.reason, 'Impossible de charger les relevés'));
    setMethodes(lesMethodes.status === 'fulfilled' ? lesMethodes.value : []);
    setImports(lesImports.status === 'fulfilled' ? lesImports.value.items : []);
    setTotalImports(lesImports.status === 'fulfilled' ? lesImports.value.total : 0);
    setChargement(false);
  }, [page, taille, nouvelleDemande]);

  useEffect(() => {
    charger();
  }, [charger]);

  const optionsMoyens = useMemo(() => MOYENS_MOBILES.map((code) => ({ value: code, label: libelle('station_paiement', code) })), []);
  const optionsStations = useMemo(
    () => [
      { value: TOUS, label: 'Toutes les stations' },
      ...stations.filter((station) => !station.archived_at).map((station) => ({ value: station.id, label: nomLisible(station.name, station.code) })),
    ],
    [stations],
  );

  const importer = async (event) => {
    event.preventDefault();
    if (!fichier) return;
    setEnCours(true);
    try {
      const contenu = await lireFichier(fichier);
      const cree = await stationsApi.credit.imports.create({
        methodCode: methode,
        stationId: stationId === TOUS ? undefined : stationId,
        filename: fichier.name,
        contentBase64: contenu,
      });
      toast.success(`${accorde(cree.matched_count, 'somme rapprochée', 'sommes rapprochées')} sur ${cree.line_count}`);
      setFichier(null);
      event.target.reset?.();
      pagination.allerA(1);
      await charger();
      setDetail(cree);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’importer le relevé'));
    } finally {
      setEnCours(false);
    }
  };

  const ouvrir = async (ligne) => {
    try {
      setDetail(await stationsApi.credit.imports.get(ligne.id));
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’ouvrir le relevé'));
    }
  };

  const rapprocher = async () => {
    setEnCours(true);
    try {
      setDetail(await stationsApi.credit.imports.match(detail.id));
      await charger();
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de rapprocher le relevé'));
    } finally {
      setEnCours(false);
    }
  };

  if (chargement) return <LoadingState shape="table" />;
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
      <div className="grid content-start gap-4">
        {peutEcrire && (
          <Panneau
            titre="Importer un relevé"
            aide={
              <Aide titre="Relevé de paiement mobile">
                Fichier CSV exporté de l’espace marchand Orange Money, Moov Money ou Wave. Chaque somme reçue est rapprochée
                d’une vente de même montant, à 10 minutes près ou de même référence.
              </Aide>
            }
          >
            <form onSubmit={importer} className="flex flex-wrap items-end gap-3 px-3">
              <Champ id="import-moyen" label="Moyen">
                <SearchableSelect id="import-moyen" value={methode} onValueChange={setMethode} options={optionsMoyens} aria-label="Moyen de paiement" className="h-9 w-44" />
              </Champ>
              <Champ id="import-station" label="Station">
                <SearchableSelect id="import-station" value={stationId} onValueChange={setStationId} options={optionsStations} aria-label="Station" searchPlaceholder="Rechercher une station…" className="h-9 w-56" />
              </Champ>
              <Champ id="import-fichier" label="Fichier">
                <Input
                  id="import-fichier"
                  type="file"
                  accept=".csv,.txt,text/csv"
                  onChange={(event) => setFichier(event.target.files?.[0] || null)}
                  className="w-64"
                />
              </Champ>
              <Button type="submit" disabled={enCours || !fichier}>
                <Upload className="h-4 w-4" aria-hidden="true" />
                Importer
              </Button>
            </form>
          </Panneau>
        )}
        <Panneau titre="Relevés importés">
          {imports.length === 0 ? (
            <Vide texte="Aucun relevé importé" />
          ) : (
            <table className={CELLULES}>
              <thead>
                <tr>
                  <th className="text-left">Importé le</th>
                  <th className="text-left">Moyen</th>
                  <th className="text-left">Période</th>
                  <th className="text-right">Sommes</th>
                  <th className="text-right">Rapprochées</th>
                  <th className="text-right">Sans vente</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {imports.map((ligne) => (
                  <tr key={ligne.id}>
                    <td className="whitespace-nowrap">{formatDateHeure(ligne.created_at)}</td>
                    <td>{libelle('station_paiement', ligne.method_code)}</td>
                    <td className="whitespace-nowrap">
                      {ligne.period_start ? `${formatDate(ligne.period_start)} → ${formatDate(ligne.period_end)}` : '—'}
                    </td>
                    <td className="text-right tabular-nums">{formatNombre(ligne.line_count)}</td>
                    <td className="text-right tabular-nums">{formatNombre(ligne.matched_count)}</td>
                    <td className="text-right tabular-nums">
                      {ligne.unmatched_count ? <Badge variant="warning">{formatNombre(ligne.unmatched_count)}</Badge> : '0'}
                    </td>
                    <td className="text-right">
                      <Button type="button" variant="outline" size="sm" onClick={() => ouvrir(ligne)}>
                        Voir
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <BarrePagination pagination={pagination} nom="relevés" className="mx-3" />
        </Panneau>
      </div>
      <MoyensPaiement methodes={methodes} peutEcrire={peutEcrire} onChange={charger} />
      <DetailImport detail={detail} onFermer={() => setDetail(null)} onRapprocher={rapprocher} peutEcrire={peutEcrire} enCours={enCours} />
    </div>
  );
};

// --- Page ------------------------------------------------------------------------------------------------------------------

const StationCreditPage = () => {
  const droits = useDroitsStation();
  const { canSeeMoney } = useAuth();
  const peutEcrire = droits.peutEcrire('station_credit');
  const { stations } = useStations();
  const [searchParams, setSearchParams] = useSearchParams();

  const onglets = useMemo(() => ONGLETS.filter(([, , argent]) => !argent || canSeeMoney), [canSeeMoney]);
  const demande = searchParams.get('onglet');
  const onglet = onglets.some(([cle]) => cle === demande) ? demande : 'comptes';
  const changerOnglet = (valeur) => {
    const suivants = new URLSearchParams(searchParams);
    suivants.set('onglet', valeur);
    setSearchParams(suivants, { replace: true });
  };

  const [clients, setClients] = useState([]);
  const [produits, setProduits] = useState([]);
  const [comptes, setComptes] = useState([]);
  const [chargementComptes, setChargementComptes] = useState(true);
  const [clientId, setClientId] = useState('');
  const [compteDialogue, setCompteDialogue] = useState({ ouvert: false, compte: null });
  const [emissionOuverte, setEmissionOuverte] = useState(false);

  const [bons, setBons] = useState([]);
  const [totalBons, setTotalBons] = useState(0);
  const [chargementBons, setChargementBons] = useState(false);
  const [filtreBons, setFiltreBons] = useState({ clientId: '', status: '', search: '' });
  // La recherche se fait côté serveur (numéro ou client): on attend la fin de la frappe.
  const rechercheBons = useDebouncedValue(filtreBons.search);
  const paginationBons = usePaginationServeur(totalBons, {
    cle: 'bons',
    reinitialiser: `${filtreBons.clientId}|${filtreBons.status}|${rechercheBons.trim()}`,
  });
  const { page: pageBons, taille: tailleBons } = paginationBons;
  const [selection, setSelection] = useState(new Set());
  const [dernierLot, setDernierLot] = useState([]);

  const chargerComptes = useCallback(async () => {
    try {
      setComptes(await stationsApi.credit.accounts.all());
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de charger les comptes crédit'));
    } finally {
      setChargementComptes(false);
    }
  }, []);

  useEffect(() => {
    chargerComptes();
    fetchAllPages((params) => clientsAPI.getAllPaged(params))
      .then(({ items }) => setClients(items))
      .catch(() => setClients([]));
    stationsApi.products
      .list({ includeInactive: false })
      .then(setProduits)
      .catch(() => setProduits([]));
  }, [chargerComptes]);

  useEffect(() => {
    if (!clientId && comptes.length) setClientId(comptes[0].id);
  }, [clientId, comptes]);

  const nouvelleDemandeBons = useDerniereDemande();
  const chargerBons = useCallback(async () => {
    const perimee = nouvelleDemandeBons();
    setChargementBons(true);
    try {
      const reponse = await stationsApi.credit.vouchers.page({
        client_id: filtreBons.clientId || undefined,
        status: filtreBons.status || undefined,
        search: rechercheBons.trim() || undefined,
        page: pageBons,
        page_size: tailleBons,
      });
      if (perimee()) return;
      setBons(reponse.items);
      setTotalBons(reponse.total);
    } catch (erreur) {
      if (perimee()) return;
      toast.error(messageErreur(erreur, 'Impossible de charger les bons'));
    } finally {
      if (!perimee()) setChargementBons(false);
    }
  }, [filtreBons.clientId, filtreBons.status, rechercheBons, pageBons, tailleBons, nouvelleDemandeBons]);

  useEffect(() => {
    if (onglet === 'bons') chargerBons();
  }, [onglet, chargerBons]);

  const imprimerBonsDe = async (ids) => {
    try {
      const planche = await stationsApi.credit.vouchers.sheet(ids);
      if (planche.length) imprimerBons(planche);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de préparer l’impression'));
    }
  };

  const apresEmission = (emis) => {
    const ids = emis.map((bon) => bon.id);
    setDernierLot(ids);
    chargerBons();
    toast.success(`${accorde(emis.length, 'bon émis', 'bons émis')}`, {
      action: { label: 'Imprimer', onClick: () => imprimerBonsDe(ids) },
      duration: 15000,
    });
  };

  const bloquer = async (compte) => {
    const bloquerMaintenant = !compte.station_credit_blocked;
    const question = bloquerMaintenant
      ? `Bloquer le compte crédit de « ${compte.name} » ? Aucune vente à crédit ne sera acceptée.`
      : `Débloquer le compte crédit de « ${compte.name} » ?`;
    if (!window.confirm(question)) return;
    try {
      await stationsApi.credit.accounts.update(compte.id, { blocked: bloquerMaintenant });
      toast.success(bloquerMaintenant ? 'Compte bloqué' : 'Compte débloqué');
      await chargerComptes();
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de modifier le compte'));
    }
  };

  const bloques = comptes.filter((compte) => compte.station_credit_blocked).length;
  const depasses = comptes.filter((compte) => compte.over_limit).length;
  const encours = comptes.reduce((somme, compte) => somme + Number(compte.outstanding || 0), 0);

  const action = (() => {
    if (!peutEcrire) return null;
    if (onglet === 'comptes' && canSeeMoney) return { label: 'Ouvrir un compte', onClick: () => setCompteDialogue({ ouvert: true, compte: null }) };
    if (onglet === 'bons') return { label: 'Émettre des bons', onClick: () => setEmissionOuverte(true) };
    return null;
  })();

  return (
    <div className="mx-auto w-full max-w-page space-y-4 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Stations"
        title="Clients et bons"
        description="Comptes crédit, flottes et véhicules, bons carburant, relevés mensuels et paiements mobiles."
        meta={comptes.length ? accorde(comptes.length, 'compte crédit', 'comptes crédit') : undefined}
        stats={[
          canSeeMoney && { label: 'Encours', valeur: formatMontant(encours) },
          bloques > 0 && { label: 'Bloqués', valeur: formatNombre(bloques), ton: 'critique' },
        ].filter(Boolean)}
        alertes={[
          depasses > 0 && {
            niveau: 'critique',
            texte: `${accorde(depasses, 'compte', 'comptes')} au-dessus du plafond de crédit`,
            court: 'Plafond dépassé',
            nombre: depasses,
            action: { label: 'Voir les comptes', onClick: () => changerOnglet('comptes') },
          },
        ].filter(Boolean)}
        actions={
          <>
            {onglet === 'bons' && dernierLot.length > 0 && (
              <Button type="button" variant="outline" onClick={() => imprimerBonsDe(dernierLot)}>
                <Printer className="h-4 w-4" aria-hidden="true" />
                Imprimer les derniers bons
              </Button>
            )}
            {action && (
              <Button type="button" onClick={action.onClick}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                {action.label}
              </Button>
            )}
          </>
        }
      />

      <Tabs value={onglet} onValueChange={changerOnglet}>
        <TabsList className="h-auto flex-wrap justify-start">
          {onglets.map(([cle, texte]) => (
            <TabsTrigger key={cle} value={cle}>
              {texte}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="comptes">
          <OngletComptes
            comptes={comptes}
            chargement={chargementComptes}
            argent={canSeeMoney}
            peutEcrire={peutEcrire}
            onModifier={(compte) => setCompteDialogue({ ouvert: true, compte })}
            onBloquer={bloquer}
            onFlotte={(id) => {
              setClientId(id);
              changerOnglet('flottes');
            }}
            onReleve={(id) => {
              setClientId(id);
              changerOnglet('releves');
            }}
          />
        </TabsContent>

        <TabsContent value="flottes">
          <OngletFlottes comptes={comptes} clientId={clientId} onClient={setClientId} produits={produits} peutEcrire={peutEcrire} />
        </TabsContent>

        <TabsContent value="bons">
          <OngletBons
            bons={bons}
            total={totalBons}
            pagination={paginationBons}
            chargement={chargementBons}
            clients={clients}
            produits={produits}
            peutEcrire={peutEcrire}
            filtre={filtreBons}
            onFiltre={setFiltreBons}
            selection={selection}
            onSelection={setSelection}
            onRecharger={chargerBons}
            onImprimer={imprimerBonsDe}
          />
        </TabsContent>

        {canSeeMoney && (
          <TabsContent value="releves">
            <OngletReleves comptes={comptes} clientId={clientId} onClient={setClientId} peutEcrire={peutEcrire} />
          </TabsContent>
        )}

        {canSeeMoney && (
          <TabsContent value="paiements">
            <OngletPaiements stations={stations} peutEcrire={peutEcrire} />
          </TabsContent>
        )}
      </Tabs>

      <CompteDialog
        open={compteDialogue.ouvert}
        onOpenChange={(ouvert) => !ouvert && setCompteDialogue({ ouvert: false, compte: null })}
        clients={clients}
        compte={compteDialogue.compte}
        peutFermer={peutEcrire}
        onSaved={(id) => {
          setClientId(id);
          chargerComptes();
        }}
      />
      <EmettreDialog
        open={emissionOuverte}
        onOpenChange={setEmissionOuverte}
        clients={clients}
        produits={produits}
        argent={canSeeMoney}
        onIssued={apresEmission}
      />
    </div>
  );
};

export default StationCreditPage;
